import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Camera, BadgePercent } from "lucide-react";
import { Reveal, Hud, EASE } from "./Reveal";
import { SectionStrip } from "./SectionStrip";
import { PROMO, PromoDetails } from "./PromoBanner";

const CountUp = ({ to, prefix = "$" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 0.9, ease: EASE, onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{prefix}{val}</span>;
};

const TIERS = [
  { name: "Minimum Load", min: 100, max: null, prefix: "from ", fits: "A few single items — a mattress, a chair, some boxes.", slug: "minimum" },
  { name: "Quarter Load", min: 150, max: 275, fits: "A small room's worth — a dresser, boxes, small appliances.", slug: "quarter" },
  { name: "Half Load", min: 325, max: 450, fits: "A full room, or the clutter from a one-car garage.", slug: "half", booked: true },
  { name: "Three-Quarter Load", min: 475, max: 550, fits: "Multi-room cleanouts and large furniture sets.", slug: "three-quarter" },
  { name: "Full Truck", min: 625, max: 750, fits: "Whole-home, estate, or full property cleanouts.", slug: "full" },
];

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5 md:px-8">
      <Reveal>
        <Hud>Honest Pricing</Hud>
        <h2 className="mt-5 font-heading text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
          Transparent Load-Based <span className="text-gold">Pricing.</span>
        </h2>
        <p className="mt-5 max-w-xl text-base font-medium capitalize leading-relaxed text-warmgray md:text-lg">
          Pay by the trailer space your junk takes — that's it. Every price confirmed face-to-face before work begins.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {TIERS.map((t, i) => (
          <Reveal key={t.slug} delay={i * 0.06} className="h-full">
            <div
              data-testid={`pricing-card-${t.slug}`}
              className={`relative flex h-full flex-col border-2 p-6 transition-all duration-200 hover:-translate-y-1 ${
                t.booked ? "border-gold bg-panel shadow-hard-gold" : "grit-card"
              }`}
            >
              {t.booked && <div className="hazard-thin absolute left-0 top-0 h-2 w-full" aria-hidden="true" />}
              <p className={`font-body text-xs font-bold uppercase tracking-[0.2em] ${t.booked ? "text-gold" : "text-warmgray"} ${t.booked ? "mt-2" : ""}`}>{t.name}</p>
              {t.booked && (
                <p data-testid="pricing-most-booked-tag" className="mt-3 w-fit bg-gold px-2.5 py-1 font-body text-[11px] font-bold uppercase tracking-[0.15em] text-ink">
                  Most Booked
                </p>
              )}
              <p
                data-testid={`pricing-price-${t.slug}`}
                className="mt-5 font-heading text-4xl uppercase leading-none tracking-wide text-white md:text-5xl"
              >
                {t.prefix && <span className="mr-1 font-body text-sm font-bold normal-case text-warmgray">{t.prefix}</span>}
                <span className="text-gold"><CountUp to={t.min} /></span>
                {t.max && <span className="text-warmgray">–</span>}
                {t.max && <span className="text-gold"><CountUp to={t.max} /></span>}
              </p>
              <p className="mt-4 text-xs capitalize leading-relaxed text-warmgray">{t.fits}</p>
              <span className="mt-auto block pt-5">
                <span className={`block h-1 ${t.booked ? "hazard-thin w-full" : "w-10 bg-gold/50"}`} aria-hidden="true" />
              </span>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div data-testid="pricing-promo" className="relative mt-10 border-2 border-gold bg-panel p-7 shadow-hard-gold md:p-9">
          <div className="hazard-thin absolute left-0 top-0 h-2 w-full" aria-hidden="true" />
          <div className="mt-2 flex items-start gap-4">
            <BadgePercent size={30} strokeWidth={1.6} className="mt-1 shrink-0 text-gold" />
            <div>
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-white/60">First-Haul Offer · New Customers</p>
              <p data-testid="pricing-promo-headline" className="mt-1 font-heading text-3xl uppercase leading-none tracking-wide text-gold md:text-4xl">
                {PROMO.headline}
              </p>
              <PromoDetails testid="pricing-promo" />
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.12em] text-white/70">
                Example: A first haul quoted at $325 (Half Load) comes to $275 with the offer.
              </p>
            </div>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.2}>
        <p data-testid="pricing-note" className="mt-10 max-w-2xl text-sm capitalize leading-relaxed text-warmgray">
          Final price depends on volume, weight, and labor — and you'll always know it before we begin. No hidden fees. Ever.
        </p>
      </Reveal>
      <SectionStrip
        icon={Camera}
        title="Not Sure Which Load?"
        text="Text us a photo of the pile and we'll come back with an exact price — free, fast, and no obligation. What we quote is what you pay."
        testid="pricing-strip"
      />
    </div>
  </section>
);
